import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Receipt, Plus, Trash2, Calendar, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExpenseTracker() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    category: "other",
    is_deductible: false,
  });

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error("Error loading expenses:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const categorizeWithAI = async () => {
    if (!formData.description) {
      toast.error("Please enter a description first");
      return;
    }

    setCategorizing(true);
    try {
      const response = await axios.post(`${API}/ai-categorize`, {
        description: formData.description,
      });
      setFormData({
        ...formData,
        category: response.data.category,
        is_deductible: response.data.is_deductible,
      });
      toast.success(`AI suggests: ${response.data.category} (${response.data.confidence} confidence)`);
    } catch (error) {
      console.error("Error categorizing:", error);
      toast.error("AI categorization unavailable");
    } finally {
      setCategorizing(false);
    }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/expenses`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success("Expense added successfully");
      setDialogOpen(false);
      setFormData({
        description: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        category: "other",
        is_deductible: false,
      });
      loadExpenses();
    } catch (error) {
      console.error("Error adding expense:", error);
      toast.error("Failed to add expense");
    }
  };

  const deleteExpense = async (id) => {
    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success("Expense deleted");
      loadExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  const totalExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);
  const deductibleExpenses = expenses.filter((e) => e.is_deductible).reduce((sum, item) => sum + item.amount, 0);

  const categories = [
    { value: "office_supplies", label: "Office Supplies" },
    { value: "software", label: "Software" },
    { value: "meals", label: "Meals" },
    { value: "transportation", label: "Transportation" },
    { value: "utilities", label: "Utilities" },
    { value: "professional_services", label: "Professional Services" },
    { value: "marketing", label: "Marketing" },
    { value: "rent", label: "Rent" },
    { value: "equipment", label: "Equipment" },
    { value: "other", label: "Other" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start"
      >
        <div>
          <h1 className="text-4xl sm:text-5xl font-heading font-light tracking-tight" data-testid="expense-tracker-title">
            Expense Tracker
          </h1>
          <p className="text-lg text-muted-foreground mt-2">Track and categorize your expenses</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-expense-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={addExpense} className="space-y-4">
              <div>
                <Label htmlFor="description">Description *</Label>
                <div className="flex space-x-2">
                  <Input
                    id="description"
                    placeholder="e.g., Office supplies, Software subscription"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="flex-1"
                    data-testid="expense-description-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={categorizeWithAI}
                    disabled={categorizing}
                    data-testid="ai-categorize-button"
                  >
                    {categorizing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  data-testid="expense-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deductible"
                  checked={formData.is_deductible}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_deductible: checked })}
                  data-testid="expense-deductible-checkbox"
                />
                <Label htmlFor="deductible" className="cursor-pointer">
                  Tax deductible
                </Label>
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-expense-button">
                Add Expense
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-accent text-accent-foreground">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-2">Total Expenses (YTD)</p>
                <p className="text-4xl font-mono font-bold" data-testid="total-expenses-amount">
                  ${totalExpenses.toLocaleString()}
                </p>
              </div>
              <Receipt className="w-16 h-16 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary text-primary-foreground">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-2">Deductible Expenses</p>
                <p className="text-4xl font-mono font-bold" data-testid="deductible-expenses-amount">
                  ${deductibleExpenses.toLocaleString()}
                </p>
              </div>
              <Sparkles className="w-16 h-16 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses List */}
      <div className="space-y-4">
        {expenses.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No expenses recorded yet</h3>
              <p className="text-muted-foreground mb-6">Start tracking your expenses and find deductions</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-state-add-expense">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Expense
              </Button>
            </CardContent>
          </Card>
        ) : (
          expenses.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-md transition-all" data-testid={`expense-item-${item.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium">{item.description}</h3>
                        <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">
                          {categories.find((c) => c.value === item.category)?.label || item.category}
                        </span>
                        {item.is_deductible && (
                          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full flex items-center">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Deductible
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(item.date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <p className="text-2xl font-mono font-semibold" data-testid={`expense-amount-${item.id}`}>
                        ${item.amount.toLocaleString()}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteExpense(item.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-expense-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}